import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, CircularProgress, Stack, Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Button, Divider, Collapse, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';
import { light } from '@mui/material/styles/createPalette';

const copy = async (text = '') => { try { await navigator.clipboard.writeText(text); } catch {} };
const mask = (v = '') => (String(v).length > 8 ? `${String(v).slice(0, 6)}…` : '•••');

const normalizeHeaders = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.flatMap((obj) => Object.entries(obj || {}).map(([k, v]) => ({ key: k, value: v })));
  }
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([k, v]) => ({ key: k, value: v }));
  }
  return [{ key: 'Header', value: String(raw) }];
};

const detectFormat = (txt = '') => {
  const s = String(txt).trim();
  if (!s) return { format: 'unknown' };
  // JSON?
  try {
    const obj = JSON.parse(s);
    return { format: 'json', json: obj };
  } catch {}
  // rudimentary YAML sniff
  if (/^\s*\w+:\s*/.test(s) || /openapi:\s*\d/.test(s) || /swagger:\s*2/.test(s)) {
    return { format: 'yaml' };
  }
  return { format: 'unknown' };
};

const getOpenApiMeta = (txt = '') => {
  const encSize = (() => {
    try { return new TextEncoder().encode(txt).length; } catch { return txt.length; }
  })();
  const lines = String(txt).split(/\r?\n/).length;
  const det = detectFormat(txt);
  const meta = { format: det.format, size: encSize, lines, openapiVersion: null, pathsCount: null, operationsCount: null };
  if (det.format === 'json' && det.json && typeof det.json === 'object') {
    const j = det.json;
    meta.openapiVersion = j.openapi || j.swagger || null;
    if (j.paths && typeof j.paths === 'object') {
      const ops = ['get','post','put','delete','patch','options','head','trace'];
      const paths = Object.keys(j.paths || {});
      meta.pathsCount = paths.length;
      meta.operationsCount = paths.reduce((acc, p) => {
        const o = j.paths[p] || {};
        return acc + ops.reduce((n, m) => n + (o[m] ? 1 : 0), 0);
      }, 0);
    }
  }
  return meta;
};

const CodeBlock = ({ language, value }) => (
  <Box sx={{ position: 'relative' }}>
    <IconButton
      size="small"
      onClick={() => copy(value)}
      sx={{ position:'absolute', right:6, top:6, zIndex:1, bgcolor:'rgba(0,0,0,0.04)' }}
      title="Copy"
    >
      <ContentCopyIcon fontSize="inherit" />
    </IconButton>
    <SyntaxHighlighter
      language={language || 'json'}
      style={prism}
      wrapLines
      customStyle={{ borderRadius: 8, margin: 0, fontSize: '0.9rem' }}
    >
      {value}
    </SyntaxHighlighter>
  </Box>
);

const ApiPentestScope = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [scopeData, setScopeData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const [showSwagger, setShowSwagger] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchScope = async (token) => {
    try {
      const res = await fetch(`${BASEURL}/pentests/api/scope?pentest_id=${pentest_id}`, {
        method:'GET',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }
      });
      const data = await res.json();
      if (data.error) {
        if (data.message === 'Invalid token') navigate('/');
        setError(data.message);
      } else {
        setScopeData(data.scope || {});
      }
    } catch (err) {
      console.error('Error fetching scope:', err);
      setError('Failed to fetch scope.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) navigate('/');
    else fetchScope(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pentest_id]);

  const headers = useMemo(() => normalizeHeaders(scopeData?.auth_headers), [scopeData]);
  const swaggerMeta = useMemo(() => getOpenApiMeta(scopeData?.swagger || ''), [scopeData?.swagger]);

  const previewText = useMemo(() => {
    const raw = String(scopeData?.swagger || '');
    const maxLines = 80;
    const lines = raw.split(/\r?\n/);
    const cut = lines.slice(0, maxLines).join('\n');
    return { cut, hasMore: lines.length > maxLines };
  }, [scopeData?.swagger]);

  const sampleCurl = useMemo(() => {
    const base = (scopeData?.api_url || '').replace(/\/+$/,'');
    const hdrs = headers.map(h => `  -H '${h.key}: ${typeof h.value === 'object' ? JSON.stringify(h.value) : h.value}'`).join(' \\\n');
    return [
      `curl -X GET \\`,
      `  '${base || 'https://api.example.com'}'${hdrs ? ' \\' : ''}`,
      hdrs
    ].filter(Boolean).join('\n');
  }, [scopeData?.api_url, headers]);

  const openSwaggerNewTab = () => {
    const txt = String(scopeData?.swagger || '');
    try {
      const blob = new Blob([txt], { type: swaggerMeta.format === 'json' ? 'application/json' : 'text/yaml' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {}
  };

  const downloadSwagger = () => {
    const txt = String(scopeData?.swagger || '');
    try {
      const blob = new Blob([txt], { type: swaggerMeta.format === 'json' ? 'application/json' : 'text/yaml' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = swaggerMeta.format === 'json' ? 'openapi.json' : 'openapi.yaml';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 2000);
    } catch {}
  };

  return (
    <Box sx={{ height:'100vh', display:'flex', flexDirection:'column', bgcolor:lighten(PRIMARYCOLOR,0.98) }}>
      <Nav />

      <Box sx={{ flexGrow:1, p:{ xs:2, md:3 }, overflowY:'auto' }}>
        <Stack direction={{ xs:'column', md:'row' }} alignItems={{ xs:'flex-start', md:'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              API Pentest Scope
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Base URL, auth headers, and Swagger/OpenAPI definition.
            </Typography>
          </Box>
        </Stack>

        {/* Loading / Error */}
        {loading ? (
          <Box sx={{ display:'flex', justifyContent:'center', mt:6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
            <Typography color="error">{error}</Typography>
          </Card>
        ) : (
          <>
            {/* KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
                  <Typography variant="overline" color="text.secondary">BASE URL</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 800, wordBreak:'break-all' }}>
                      {scopeData?.api_url || '—'}
                    </Typography>
                    {scopeData?.api_url && (
                      <Tooltip title="Copy URL">
                        <IconButton size="small" onClick={()=>copy(scopeData.api_url)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={6} md={3} lg={2}>
                <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
                  <Typography variant="overline" color="text.secondary">AUTH HEADERS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{headers.length}</Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={3} lg={2}>
                <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
                  <Typography variant="overline" color="text.secondary">SWAGGER FORMAT</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={(swaggerMeta.format || 'unknown').toUpperCase()} />
                    {swaggerMeta.openapiVersion && (
                      <Chip size="small" label={String(swaggerMeta.openapiVersion)} variant="outlined" />
                    )}
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={6} md={3} lg={2}>
                <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
                  <Typography variant="overline" color="text.secondary">SIZE</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {(swaggerMeta.size/1024).toFixed(1)} KB · {swaggerMeta.lines} lines
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={3} lg={2}>
                <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
                  <Typography variant="overline" color="text.secondary">PATHS / OPS</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {(swaggerMeta.pathsCount ?? '—')} / {(swaggerMeta.operationsCount ?? '—')}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Auth Headers */}
            <Card sx={{ p:2, borderRadius:3, boxShadow:2, mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Auth Headers</Typography>
                {headers.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => copy(JSON.stringify(Object.fromEntries(headers.map(h=>[h.key,h.value])), null, 2))}
                    >
                      Copy JSON
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => copy(headers.map(h => `-H '${h.key}: ${typeof h.value === 'object' ? JSON.stringify(h.value) : h.value}'`).join(' '))}
                    >
                      Copy as curl -H
                    </Button>
                  </Stack>
                )}
              </Stack>

              {headers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No auth headers provided.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius:2, boxShadow:0, border:'1px solid #eee' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight:'bold' }}>Header</TableCell>
                        <TableCell sx={{ fontWeight:'bold' }}>Value</TableCell>
                        <TableCell sx={{ fontWeight:'bold' }} width={80} align="right">Copy</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {headers.map((h, idx) => {
                        const asText = typeof h.value === 'object' ? JSON.stringify(h.value) : String(h.value ?? '');
                        const masked = mask(asText);
                        return (
                          <TableRow key={`${h.key}-${idx}`} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{h.key}</TableCell>
                            <TableCell sx={{ wordBreak:'break-all' }}>
                              <Typography variant="body2" color="text.secondary">{masked}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Copy value">
                                <IconButton size="small" onClick={() => copy(asText)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>

            {/* Quick cURL example */}
            <Card sx={{ p:2, borderRadius:3, boxShadow:2, mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Quick Test (cURL)</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => copy(sampleCurl)}>Copy command</Button>
                </Stack>
              </Stack>
              <CodeBlock language="bash" value={sampleCurl} />
            </Card>

            {/* Swagger / OpenAPI */}
            <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Swagger / OpenAPI</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen((v)=>!v)}>
                    {previewOpen ? 'Hide Preview' : 'Preview'}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={openSwaggerNewTab}>
                    Open in new tab
                  </Button>
                  <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={downloadSwagger}>
                    Download
                  </Button>
                </Stack>
              </Stack>

              <Collapse in={previewOpen} timeout="auto" unmountOnExit>
                <Divider sx={{ my: 1.5 }} />
                {scopeData?.swagger ? (
                  <CodeBlock
                    language={swaggerMeta.format === 'json' ? 'json' : (swaggerMeta.format === 'yaml' ? 'yaml' : 'text')}
                    value={previewText.cut + (previewText.hasMore ? '\n…\n# (Preview truncated)' : '')}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No swagger definition provided.</Typography>
                )}
                {scopeData?.swagger && previewText.hasMore && (
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => setShowSwagger(true)}>View full</Button>
                  </Box>
                )}
              </Collapse>
            </Card>

            {/* Full Swagger dialog */}
            <Dialog open={showSwagger} onClose={()=>setShowSwagger(false)} maxWidth="lg" fullWidth>
              <DialogTitle sx={{ pr: 6 }}>
                Full Swagger / OpenAPI
                <IconButton onClick={()=>setShowSwagger(false)} sx={{ position:'absolute', right:8, top:8 }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {scopeData?.swagger ? (
                  <CodeBlock
                    language={swaggerMeta.format === 'json' ? 'json' : (swaggerMeta.format === 'yaml' ? 'yaml' : 'text')}
                    value={String(scopeData.swagger)}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No swagger definition provided.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={openSwaggerNewTab}>Open in new tab</Button>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadSwagger}>Download</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ApiPentestScope;
